/**
 * The contact directory, and who is told about which work.
 *
 * Every stakeholder and contractor is an organisation; the people in it are
 * contacts; each person has as many ways to be reached as they have — several
 * mobiles, a landline, several emails, a WhatsApp number — each labelled, one
 * of each kind primary, and each switched on or off for messages. A message
 * for a person goes to every switched-on point of that kind.
 *
 * A notification rule names work order events, which work it matches, who is
 * told (people, organisations, dispatch teams) and by which channels, and who
 * is told if nobody starts the work in time. The platform applies the rules
 * the moment a work order is raised or changes state, and the clock raises
 * "overdue" and "escalated" once each.
 */
/** +9665XXXXXXXX from the ways a Saudi mobile is written; other numbers as given in E.164 (hr/notify.py `saudi_mobile`). */
export function saudiMobile(phone: string | undefined | null): string | null {
  if (!phone) return null;
  let p = phone.replace(/[^\d+]/g, "");
  if (p.startsWith("00")) p = "+" + p.slice(2);
  if (/^05\d{8}$/.test(p)) return "+966" + p.slice(1);
  if (/^5\d{8}$/.test(p)) return "+966" + p;
  if (/^9665\d{8}$/.test(p)) return "+" + p;
  if (/^\+\d{8,15}$/.test(p)) return p;
  return null;
}

export type OrgKind = "contractor" | "agency" | "airline" | "in_house" | "supplier" | "authority" | "other";
export type PointKind = "mobile" | "phone" | "email" | "whatsapp";
export type Channel = "sms" | "email" | "app";
export type NoticeEvent =
  | "created"
  | "approved"
  | "dispatched"
  | "in_progress"
  | "on_hold"
  | "completed"
  | "verified"
  | "cancelled"
  | "overdue"
  | "escalated";

export const ORG_KIND_LABEL: Record<OrgKind, string> = {
  contractor: "Contractor",
  agency: "Agency",
  airline: "Airline",
  in_house: "In house",
  supplier: "Supplier",
  authority: "Authority",
  other: "Other",
};

export const POINT_KIND_LABEL: Record<PointKind, string> = {
  mobile: "Mobile",
  phone: "Landline",
  email: "Email",
  whatsapp: "WhatsApp",
};

export const EVENT_LABEL: Record<NoticeEvent, string> = {
  created: "Raised",
  approved: "Approved",
  dispatched: "Dispatched",
  in_progress: "Started",
  on_hold: "On hold",
  completed: "Completed",
  verified: "Verified",
  cancelled: "Canceled",
  overdue: "Overdue",
  escalated: "Escalated",
};
export const EVENTS = Object.keys(EVENT_LABEL) as NoticeEvent[];

export const CHANNEL_LABEL: Record<Channel, string> = { sms: "SMS", email: "Email", app: "Mobile app" };

export interface ContactPoint {
  id?: string;
  kind: PointKind;
  value: string;
  label?: string;
  primary: boolean;
  notify: boolean;
}

export interface ContactPerson {
  id?: string;
  orgId?: string;
  name: string;
  role?: string;
  language: "ar" | "en";
  preferred: Channel;
  /** the platform sign-in that gets alerts in the mobile app */
  principalId?: string;
  employeeId?: string;
  hours?: string;
  notes?: string;
  active: boolean;
  points: ContactPoint[];
}

export interface ContactOrg {
  id?: string;
  siteId?: string;
  name: string;
  kind: OrgKind;
  notes?: string;
  active: boolean;
  people: ContactPerson[];
}

export interface Directory {
  orgs: ContactOrg[];
  /** people who belong to no organisation */
  people: ContactPerson[];
  channels?: { sms: boolean; email: boolean };
}

export type RecipientType = "person" | "org" | "group";
export interface Recipient {
  type: RecipientType;
  id: string;
}

export interface RuleMatch {
  priorities?: number[];
  kinds?: string[];
  zoneCodes?: string[];
  dispatchGroups?: string[];
}

export interface NotifyRule {
  id?: string;
  siteId: string;
  name: string;
  active: boolean;
  events: NoticeEvent[];
  match: RuleMatch;
  recipients: Recipient[];
  channels: Channel[];
  escalateAfterMin?: number;
  escalateTo: Recipient[];
}

export interface Reach {
  name: string;
  sms: string[];
  email: string[];
  app: boolean;
}

export interface WorkOrderMessage {
  id: string;
  recipient: string;
  channel: Channel;
  to?: string;
  status: string;
  error?: string;
  event?: string;
  createdAt?: string;
  sentAt?: string;
  readAt?: string;
  rule?: string;
  group?: string;
}

/** A point as the platform stores it: mobiles in +966 form; a bad one is refused before it is saved. */
export function checkPoint(p: ContactPoint): { ok: true; value: string } | { ok: false; why: string } {
  const v = p.value.trim();
  if (p.kind === "mobile" || p.kind === "whatsapp") {
    const m = saudiMobile(v);
    return m ? { ok: true, value: m } : { ok: false, why: `${v} is not a mobile number that can take a message` };
  }
  if (p.kind === "email")
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)
      ? { ok: true, value: v }
      : { ok: false, why: `${v} is not an email address` };
  return v.length >= 3 ? { ok: true, value: v } : { ok: false, why: `${v} is too short` };
}

/** One primary of each kind: the one chosen, else the first. */
export function normalisePoints(points: readonly ContactPoint[]): ContactPoint[] {
  const seen = new Set<PointKind>();
  const out = points.map((p) => {
    const primary = p.primary && !seen.has(p.kind);
    if (primary) seen.add(p.kind);
    return { ...p, primary };
  });
  for (const k of new Set(out.map((p) => p.kind)))
    if (!seen.has(k)) {
      const first = out.find((p) => p.kind === k);
      if (first) first.primary = true;
    }
  return out;
}

/** Where a person is reached: every switched-on mobile and email, and the app if signed in. */
export function reachOf(p: ContactPerson): Reach {
  const on = p.points.filter((x) => x.notify);
  return {
    name: p.name,
    sms: on.filter((x) => x.kind === "mobile" || x.kind === "whatsapp").map((x) => x.value),
    email: on.filter((x) => x.kind === "email").map((x) => x.value),
    app: !!p.principalId,
  };
}

/** Whether a rule covers a work order. */
export function ruleMatches(
  rule: NotifyRule,
  wo: { priority: number; kind: string; zoneCode?: string; crew?: string; dispatchGroup?: string },
): boolean {
  const m = rule.match;
  if (m.priorities?.length && !m.priorities.includes(wo.priority)) return false;
  if (m.kinds?.length && !m.kinds.includes(wo.kind)) return false;
  if (m.zoneCodes?.length && !m.zoneCodes.some((z) => (wo.zoneCode ?? "").startsWith(z))) return false;
  if (m.dispatchGroups?.length && !m.dispatchGroups.includes(wo.dispatchGroup ?? "")) return false;
  return true;
}
